<template>
  <div>
    <section v-if="isLoading" :class="$style.slotsCollectionsWrapper">
      <h1 :class="$provider.providersGamesTitle">{{ data.collectionsTitle }}</h1>
      <p :class="$provider.providersGamesSubTitle">{{ data.collectionsSubtitle }}</p>

      <div :class="$provider.formSearchProviders">
        <BitSearchInput
          :class="$provider.searchInputProviders"
          no-search-errors
          :autofocus="false"
          :no-results-found="isNoResultsFound"
          :placeholder="data.plaseholderCollection"
          :input-value="searchStringCollections"
          :no-found-text="storeComponents.search.noFoundText"
          :search-help-text="storeComponents.search.searchHelpText"
          @searchChanged="searchStringCollections = $event"
        />
        <BitSelect
          v-model="currentSort"
          :class="$provider.selectedProvider"
          :placeholder="data.placeholderSort"
          is-no-margin
          variant="filter"
          :options="collectionsSortOptions"
        >
          <template #appendInputIcon>
            <img src="/images/common/ico_iconSort.svg??url_encode" />
          </template>
        </BitSelect>
      </div>
      <template v-if="!hasSearchBeenMade">
        <template v-if="showTopCollectionList">
          <h2 :class="$provider.headingProvider">
            {{ data.collectionsTopSlot }}
          </h2>
          <div :class="$style.slotsCollections">
            <BitCollectionCard
              v-for="item in collections.topCollections"
              :key="item.categoryId"
              :class="$style.slotsCollectionsItem"
              :image-url="`${item.image?.url}?w=265&q=90&fit=cover&format=webp`"
              @click.prevent="setSelectedCollections(item)"
            >
              <template #custom-title>
                <NuxtLink
                  :to="`${item.isDefaultCategory ? '' : '/slot-themes'}/${item.categoryId}`"
                >
                  {{ item.title }}
                </NuxtLink>
              </template>
            </BitCollectionCard>
          </div>
        </template>
        <h2 :class="$provider.headingProvider">
          {{ data.collectionsAllSlot }}
        </h2>
        <div :class="$style.slotsCollections">
          <BitCollectionCard
            v-for="item in collections.allCollections"
            :key="item.categoryId"
            :class="$style.slotsCollectionsItem"
            :image-url="item.image?.url"
            @click.prevent="setSelectedCollections(item)"
          >
            <template #custom-title>
              <NuxtLink :to="`${item.isDefaultCategory ? '' : '/slot-themes'}/${item.categoryId}`">
                {{ item.title }}
              </NuxtLink>
            </template>
          </BitCollectionCard>
        </div>
      </template>

      <template v-else-if="isNoResultsFound">
        <h2 :class="$provider.headingProvider">{{ data.collectionsTopSlot }}</h2>
        <div :class="$style.slotsCollections">
          <BitCollectionCard
            v-for="item in collections.topCollections"
            :key="item.categoryId"
            :class="$style.slotsCollectionsItem"
            :image-url="item.image?.url"
            @click.prevent="setSelectedCollections(item)"
          >
            <template #custom-title>
              <NuxtLink :to="`${item.isDefaultCategory ? '' : '/slot-themes'}/${item.categoryId}`">
                {{ item.title }}
              </NuxtLink>
            </template>
          </BitCollectionCard>
        </div>
      </template>

      <template v-else>
        <h2 :class="$provider.headingProvider">{{ storeComponents.search.searchResult }}</h2>
        <div :class="$style.slotsCollections">
          <BitCollectionCard
            v-for="item in collectionsFound"
            :key="item.categoryId"
            :class="$style.slotsCollectionsItem"
            :image-url="item.image?.url"
            @click.prevent="setSelectedCollections(item)"
          >
            <template #custom-title>
              <span>{{ item.startTitle }}</span>
              <span :class="$style.centerTitle">{{ item.boldTitle }}</span>
              <span>{{ item.endTitle }}</span>
            </template>
          </BitCollectionCard>
        </div>
      </template>
    </section>
    <CollectionSkeleton v-else />
  </div>
</template>

<script lang="ts" setup>
import { BitCollectionCard, BitSelect, BitSearchInput } from '@bit/ui-kit';
import CollectionSkeleton from '~/components/infoPage/collectionSkeleton.vue';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreComponents } from '~/stores/strapi/components';
import { useStoreSlotTheme } from '~/stores/strapi/slotThemes';
import type { ComponentInfoPageBlocksCollections } from '~/types/infoPages';
import type { ISlotThemeData } from '~/types/slots';

enum enumCollectionsSort {
  Default = 'default',
  NameAlphabet = 'nameAlphabet',
  NameAlphabetRevers = 'nameAlphabetRevers',
  Rating = 'rating',
  Random = 'random',
}

defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksCollections>,
    required: true,
  },
});

const router = useRouter();

const storeComponents = useStoreComponents();
const loadCollections = useStoreSlotTheme();

const currentSort = ref('Default');
const searchStringCollections = ref('');
const isNoResultsFound = ref(false);
const isLoading = ref(false);
const hasSearchBeenMade = ref(false);
const showTopCollectionList = ref(true);
const collectionsSortOptions = ref({});

const collections = computed(() => {
  const activeList = loadCollections.activeList;

  return {
    allCollections: activeList,
    topCollections: activeList.slice(0, 16),
  };
});

const collectionsFound = computed(() => {
  const activeList = loadCollections.activeList;
  const searchString = searchStringCollections.value.trim().toLowerCase();

  return Object.values(activeList)
    .map((item) => {
      const collectionTitleLower = item.title.toLowerCase();
      const startIndex = collectionTitleLower.indexOf(searchString);
      const endIndex = startIndex + searchString.length;

      return {
        ...item,
        startTitle: item.title.substring(0, startIndex),
        boldTitle: item.title.substring(startIndex, endIndex),
        endTitle: item.title.substring(endIndex),
      };
    })
    .filter((item) => item.title.toLowerCase().includes(searchString));
});

const sortCollection = () => {
  collectionsSortOptions.value = {
    [enumCollectionsSort.Default]: storeComponents.sortProvider.default,
    [enumCollectionsSort.NameAlphabet]: storeComponents.sortProvider.nameAlphabet,
    [enumCollectionsSort.NameAlphabetRevers]: storeComponents.sortProvider.nameAlphabetRevers,
    [enumCollectionsSort.Rating]: storeComponents.sortProvider.rating,
    [enumCollectionsSort.Random]: storeComponents.sortProvider.random,
  };
};

const setSelectedCollections = (item: ISlotThemeData) => {
  router.push({
    path: useStoreRoot().localePath(
      `${item.isDefaultCategory ? '' : '/slot-themes'}/${item.categoryId}`
    ),
  });
};

function applySort(sortType: string) {
  const activeList = loadCollections.activeList;
  searchStringCollections.value = '';

  activeList.sort((a, b) => {
    switch (sortType) {
      case enumCollectionsSort.NameAlphabet:
        return a.title.localeCompare(b.title);
      case enumCollectionsSort.NameAlphabetRevers:
        return b.title.localeCompare(a.title);
      case enumCollectionsSort.Rating:
        return a.position - b.position;
      case enumCollectionsSort.Random:
        return 0.5 - Math.random();
      default:
        return 0;
    }
  });

  showTopCollectionList.value = sortType === enumCollectionsSort.Default;
}

watch(searchStringCollections, (newValue) => {
  const trimmedValue = newValue.trim();
  hasSearchBeenMade.value = !!trimmedValue;

  isNoResultsFound.value = !!trimmedValue && collectionsFound.value.length === 0;
});

watch(currentSort, (newSort) => {
  applySort(newSort);
});

onMounted(async () => {
  isLoading.value = false;
  await loadCollections.loadSlotThemes();
  sortCollection();
  isLoading.value = true;
});
</script>

<style lang="scss" module src="./collections.scss"></style>
<style lang="scss" module="$provider">
@import url('./providersBlock.scss');
</style>
